// =================================================================
// Account page
//
// The individual subscriber's home, parallel to /resident for
// community residents. Two main states:
//
//   - NOT entitled (no active subscription): show "Subscribe" CTA
//     that initiates Stripe Checkout via createIndividualCheckoutSessionV2.
//
//   - Entitled (active subscription): show the dashboard entry point
//     and a "Manage subscription" button that opens the Stripe
//     Customer Portal via createIndividualPortalSessionV2.
//
// Both Function calls send a Firebase ID token in the Authorization
// header so the Functions can verify the request comes from the
// authenticated user. The user.getIdToken() call returns the current
// token (Firebase refreshes it automatically as needed).
//
// Defensive routing: community residents and device users should
// never land here — they have their own dashboards — but if they do
// (manual URL nav, etc.), bounce them to the right place.
//
// Phase 2C: "Start playing" now links to /resident — the unified
// Brain Health dashboard. Subscribers without an assessment yet
// will be routed by Dashboard to /onboarding/assessment, which is
// also now identity-source-agnostic. The full assessment → goals →
// dashboard onboarding flow works end-to-end for individuals.
//
// Endpoint suffixes: the Cloud Functions backing this page are the
// Gen 2 ("V2") variants — they run on Cloud Run, read Stripe secrets
// from Secret Manager, and have better concurrency than the Gen 1
// originals they replaced. The V2 suffix in the function name is
// permanent; Firebase doesn't allow same-name v1↔v2 migration so the
// new names ship as-is. The legacy Gen 1 endpoints (without "V2")
// still exist in deployed code as a fallback during cutover but
// shouldn't be called from this page anymore.
// =================================================================

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  useCurrentContext,
  CONTEXT_TYPES,
} from '../lib/useCurrentContext.jsx';
import { useIndividualUser } from '../lib/useIndividualUser.jsx';
import { useAuth } from '../lib/useAuth.jsx';
import './Account.css';

// Firebase Functions base URL — same host for both Gen 1 and Gen 2.
// Gen 2 functions are reachable at the standard cloudfunctions.net
// hostname (as well as a generated *.run.app URL, but we use the
// stable one).
const FUNCTIONS_URL = 'https://us-central1-quiltt-games.cloudfunctions.net';

export default function Account() {
  const navigate = useNavigate();
  const { type, displayName, entitlementOk } = useCurrentContext();
  const { hasActiveSubscription } = useIndividualUser();
  const { logout, user } = useAuth();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Defensive: if a community resident or device session lands here,
  // route them to their actual dashboard. The RequireBrainHealthUser
  // guard accepts any active context; this page is individual-only.
  useEffect(() => {
    if (type === CONTEXT_TYPES.COMMUNITY_RESIDENT) {
      navigate('/resident', { replace: true });
    } else if (type === CONTEXT_TYPES.DEVICE) {
      navigate('/', { replace: true });
    }
  }, [type, navigate]);

  // If the redirect is in flight, render nothing rather than flashing
  // mismatched UI.
  if (type !== CONTEXT_TYPES.INDIVIDUAL_USER) {
    return null;
  }

  async function handleSubscribe() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const idToken = await user.getIdToken();
      const response = await fetch(
        `${FUNCTIONS_URL}/createIndividualCheckoutSessionV2`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            successUrl: `${window.location.origin}/account?subscribed=true`,
            cancelUrl: `${window.location.origin}/account?cancelled=true`,
          }),
        }
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(
          result.error || 'Could not start checkout. Please try again.'
        );
      }
      // Redirect to Stripe Checkout. The success_url returns the user
      // here with ?subscribed=true; the webhook handler will write
      // the subscription record, and useIndividualUser will reactively
      // flip entitlementOk to true.
      window.location.href = result.url;
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  }

  async function handleManageSubscription() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const idToken = await user.getIdToken();
      const response = await fetch(
        `${FUNCTIONS_URL}/createIndividualPortalSessionV2`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            returnUrl: `${window.location.origin}/account`,
          }),
        }
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(
          result.error || 'Could not open subscription management.'
        );
      }
      window.location.href = result.url;
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  }

  async function handleSignOut() {
    await logout();
    navigate('/', { replace: true });
  }

  return (
    <div className="container account-page">
      <span className="eyebrow fade-in-1">Your Account</span>
      <h1 className="account-title fade-in-2">
        Hi, <em>{displayName || 'there'}</em>.
      </h1>

      {error && (
        <div className="account-error" role="alert">
          <p className="account-error-line">{error}</p>
          <button
            type="button"
            onClick={() => setError(null)}
            className="account-error-dismiss"
            aria-label="Dismiss error"
          >
            Dismiss
          </button>
        </div>
      )}

      {!entitlementOk && (
        <section className="account-cta-section fade-in-3">
          <h2 className="account-cta-title">
            Let's get you started.
          </h2>
          <p className="account-cta-lede">
            To start your brain health journey, complete your
            subscription. $4.99/month, cancel anytime.
          </p>
          <button
            onClick={handleSubscribe}
            disabled={submitting}
            className="btn account-subscribe"
          >
            {submitting ? 'Starting checkout…' : 'Subscribe — $4.99/month'}
          </button>
          <p className="account-cta-fine">
            Powered by Stripe. We never see or store your card details.
          </p>
        </section>
      )}

      {entitlementOk && (
        <section className="account-active-section fade-in-3">
          <p className="account-status">
            <span className="account-status-dot" aria-hidden="true">●</span>
            Your subscription is <strong>active</strong>.
          </p>

          <div className="account-actions">
            <Link to="/resident" className="btn account-play">
              Start playing
            </Link>
            {hasActiveSubscription && (
              <button
                onClick={handleManageSubscription}
                disabled={submitting}
                className="btn btn-secondary account-manage"
              >
                {submitting ? 'Opening…' : 'Manage subscription'}
              </button>
            )}
          </div>
        </section>
      )}

      <button
        onClick={handleSignOut}
        className="account-signout"
        type="button"
      >
        Sign out
      </button>
    </div>
  );
}
